import Select from "react-select";
import type { ThemeClasses } from "../types";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { getStatusOptionsForEntity } from "../../../../../../utils/getStatusEntity";
import { getCustomSelectStyles, FilterPopover } from "../../../../shared";
import { useTranslation } from "react-i18next";

interface DeviceFiltersProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  selectedStatus: string;
  selectedType: string;
  searchQuery: string;
  onStatusChange: (value: string | null) => void;
  onTypeChange: (value: string | null) => void;
  onSearchChange: (value: string) => void;
  t: (key: string, options?: any) => string;
  selectedView?: string;
  onViewChange?: (view: string) => void;
}

export function DeviceFilters({
  themeClasses,
  isDarkMode,
  selectedStatus,
  selectedType,
  searchQuery,
  onStatusChange,
  onTypeChange,
  onSearchChange,
  t,
}: DeviceFiltersProps) {
  // Use common namespace for status translations
  const { t: tCommon } = useTranslation("common");

  // Opciones de estado filtradas para devices (solo activo e inactivo)
  const statuses = useStatusesStore((state) => state.statuses);
  const allStatusOptions = getStatusOptionsForEntity(
    statuses,
    "devices",
    tCommon,
  );
  const statusOptionsSelect = allStatusOptions.filter((opt) => {
    const label = opt.label.toLowerCase();
    return (
      label === "active" ||
      label === "activo" ||
      label === "inactive" ||
      label === "inactivo"
    );
  });

  // Opciones de tipo de dispositivo
  const typeOptions = [
    { value: "gps", label: "GPS" },
    { value: "phone", label: "Móvil" },
    { value: "internal", label: "Interno" },
    { value: "bluetooth", label: "Bluetooth" },
  ];

  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {/* Search Input */}
      <div className="relative flex-1 min-w-0 sm:flex-none sm:w-72">
        <i
          className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}
        ></i>
        <input
          type="text"
          placeholder={t("devices.filters.search")}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className={`w-full h-9 pl-10 pr-4 py-2 ${
            isDarkMode ? "bg-dark-card" : "bg-gray-50"
          } border ${themeClasses.cardBorder} rounded-lg ${
            themeClasses.text
          } text-sm focus:outline-none focus:ring-2 focus:ring-primary-500`}
        />
      </div>

      <FilterPopover
        isDarkMode={isDarkMode}
        activeCount={(selectedStatus ? 1 : 0) + (selectedType ? 1 : 0)}
        onClear={() => {
          onStatusChange(null);
          onTypeChange(null);
        }}
      >
        {/* Status Filter */}
        <Select
          options={statusOptionsSelect}
          styles={getCustomSelectStyles(isDarkMode)}
          placeholder={t("devices.filters.allStatus")}
          value={
            statusOptionsSelect.find((opt) => opt.value === selectedStatus) ||
            null
          }
          onChange={(option: any) => onStatusChange(option?.value || null)}
          isSearchable
          isClearable
          noOptionsMessage={() => t("devices.filters.noStatusesAvailable")}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />

        {/* Type Filter */}
        <Select
          options={typeOptions}
          styles={getCustomSelectStyles(isDarkMode)}
          placeholder="Todos los tipos"
          value={typeOptions.find((opt) => opt.value === selectedType) || null}
          onChange={(option: any) => onTypeChange(option?.value || null)}
          isSearchable
          isClearable
          noOptionsMessage={() => "Sin tipos disponibles"}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </FilterPopover>
    </div>
  );
}
